import { useContext, useEffect, useState } from "react";

import { AuthContext } from "../context/auth-context";
import api from "../BrandOnBoarding/api";

const NON_RESET_FEATURES = ["Discovery", "BrandAudit", "CategoryAnalysis"];

const MODULE_MAP = {
  BrandAudit: "brand_audit",
  Discovery: "discovery",
  BaselineDiscovery: "baseline_discovery",
  CategoryAnalysis: "categoryanalysis",
  BaselineCategoryAnalysis: "baseline_categoryanalysis",
  Conversation: "conversation",
  ContentMapping: "content_mapping",
  PromptEngine: "prompt_engine",
};

function isSameDate(date1, date2) {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}

export function useDailyLimits(feature) {
  const [currentUser, setCurrentUser] = useState();
  const [poolStatus, setPoolStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const { user } = useContext(AuthContext);
  const moduleKey = MODULE_MAP[feature];

  const fetchPoolStatus = async (dbUser) => {
    if (!dbUser?.customer_id || !moduleKey) return;
    try {
      const response = await api.get(
        `/limits/pool-status?customerId=${dbUser.customer_id}&userId=${dbUser.id}&module=${moduleKey}`
      );
      setPoolStatus(response.data);
    } catch {
      setPoolStatus(null);
    }
  };

  const getHistory = async () => {
    const response = await api.get(`/users/${user.db_id}`);
    const dbUser = response.data.data;
    setCurrentUser(dbUser);
    if (dbUser?.customer_limit_strategy === "customer") {
      await fetchPoolStatus(dbUser);
    }
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    getHistory();
  }, []);

  // --- User mode helpers ---

  function resetLimits(updatedUser) {
    const isNonReset = updatedUser.limit_frequency === "non_reset";
    if (!isNonReset) {
      updatedUser.discovery_limits_remaining = updatedUser.discovery_limits;
      updatedUser.brand_audit_limits_remaining = updatedUser.brand_audit_limits;
      updatedUser.categoryanalysis_limits_remaining = updatedUser.categoryanalysis_limits;
    }
    updatedUser.baseline_discovery_limits_remaining = updatedUser.baseline_discovery_limits;
    updatedUser.baseline_categoryanalysis_limits_remaining = updatedUser.baseline_categoryanalysis_limits;
    updatedUser.prompt_engine_limits_remaining = updatedUser.prompt_engine_limits;
    updatedUser.conversation_limits_remaining = updatedUser.conversation_limits;
    updatedUser.content_mapping_limits_remaining = updatedUser.content_mapping_limits;
    updatedUser.last_usage_date = Date.now();
  }

  function decrementFeature(updatedUser) {
    if (feature === "Discovery") updatedUser.discovery_limits_remaining -= 1;
    else if (feature === "BaselineDiscovery") updatedUser.baseline_discovery_limits_remaining -= 1;
    else if (feature === "PromptEngine") updatedUser.prompt_engine_limits_remaining -= 1;
    else if (feature === "Conversation") updatedUser.conversation_limits_remaining -= 1;
    else if (feature === "ContentMapping") updatedUser.content_mapping_limits_remaining -= 1;
    else if (feature === "BrandAudit") updatedUser.brand_audit_limits_remaining -= 1;
    else if (feature === "CategoryAnalysis") updatedUser.categoryanalysis_limits_remaining -= 1;
    else if (feature === "BaselineCategoryAnalysis") updatedUser.baseline_categoryanalysis_limits_remaining -= 1;
    updatedUser.last_usage_date = Date.now();
  }

  function buildUserModeUpdate() {
    let updatedUser = { ...currentUser };
    const freq = updatedUser.limit_frequency;
    const currentMonth = new Date().getMonth() + 1;
    const usageMonth = new Date(updatedUser.last_usage_date).getMonth() + 1;

    if (freq === "non_reset") {
      const isNonResetFeature = NON_RESET_FEATURES.includes(feature);
      const isNewMonth = currentMonth !== usageMonth;
      if (!isNonResetFeature && isNewMonth) {
        resetLimits(updatedUser);
      } else {
        decrementFeature(updatedUser);
      }
    } else {
      const currentDate = Date.now();
      const usageDate = new Date(updatedUser.last_usage_date);
      const isNewMonth = freq === "monthly" && currentMonth !== usageMonth;
      const isNewDay = freq !== "monthly" && !isSameDate(currentDate, usageDate);
      if (isNewMonth || isNewDay) {
        resetLimits(updatedUser);
      } else {
        decrementFeature(updatedUser);
      }
    }
    return updatedUser;
  }

  // --- Main update function ---

  async function updateUser(personas = null) {
    const isCustomerPool = currentUser?.customer_limit_strategy === "customer";

    if (isCustomerPool) {
      try {
        const response = await api.post("/limits/consume", {
          customerId: currentUser.customer_id,
          userId: currentUser.id,
          module: moduleKey,
        });
        setPoolStatus(response.data);
      } catch (err) {
        if (err.response?.status === 429) {
          setPoolStatus(err.response.data);
        }
      }
      if (personas && typeof personas === "object") {
        await api.patch(`/users/${user.db_id}`, {
          is_verified: currentUser?.is_verified,
          is_active: currentUser?.is_active,
          personas,
        });
        await getHistory();
      }
      return;
    }

    // User mode: existing behavior unchanged
    const updatedUser = buildUserModeUpdate();
    const payload = {
      is_verified: updatedUser.is_verified,
      is_active: updatedUser.is_active,
      discovery_limits_remaining: updatedUser.discovery_limits_remaining,
      baseline_discovery_limits_remaining: updatedUser.baseline_discovery_limits_remaining,
      prompt_engine_limits_remaining: updatedUser.prompt_engine_limits_remaining,
      conversation_limits_remaining: updatedUser.conversation_limits_remaining,
      content_mapping_limits_remaining: updatedUser.content_mapping_limits_remaining,
      brand_audit_limits_remaining: updatedUser.brand_audit_limits_remaining,
      categoryanalysis_limits_remaining: updatedUser.categoryanalysis_limits_remaining,
      baseline_categoryanalysis_limits_remaining: updatedUser.baseline_categoryanalysis_limits_remaining,
      last_usage_date: updatedUser.last_usage_date,
      ...(personas && typeof personas === "object" ? { personas } : {}),
    };
    setCurrentUser(updatedUser);
    await api.patch(`/users/${user.db_id}`, payload);
    if (personas && typeof personas === "object") {
      await getHistory();
    }
  }

  async function updatePersonasOnly(personas) {
    if (!personas || typeof personas !== "object") return;
    const payload = {
      is_verified: currentUser?.is_verified,
      is_active: currentUser?.is_active,
      personas,
    };
    await api.patch(`/users/${user.db_id}`, payload);
    await getHistory();
  }

  // Periodic reset — user mode only
  useEffect(() => {
    if (!currentUser || currentUser?.customer_limit_strategy === "customer") return;

    const freq = currentUser.limit_frequency;
    const currentMonth = new Date().getMonth() + 1;
    const usageMonth = new Date(currentUser.last_usage_date).getMonth() + 1;
    const currentDate = Date.now();
    const usageDate = new Date(currentUser.last_usage_date);

    if (freq === "non_reset") {
      // Non-reset features never auto-reset; other modules reset monthly
      if (NON_RESET_FEATURES.includes(feature)) return;
      if (currentMonth !== usageMonth) updateUser();
      return;
    }

    let isReset = false;
    if (freq === "monthly" && currentMonth !== usageMonth) {
      isReset = true;
    } else if (freq !== "monthly" && !isSameDate(currentDate, usageDate)) {
      isReset = true;
    }
    if (isReset) updateUser();
  }, [currentUser]);

  // --- Derived display values ---

  const isCustomerPool = currentUser?.customer_limit_strategy === "customer";

  let remainingCalls, apiCalls;

  if (isCustomerPool) {
    remainingCalls = poolStatus?.pool_remaining ?? null;
    apiCalls = poolStatus?.pool_limit ?? null;
  } else {
    remainingCalls =
      feature === "Discovery"
        ? currentUser?.discovery_limits_remaining
        : feature === "BaselineDiscovery"
        ? currentUser?.baseline_discovery_limits_remaining
        : feature === "PromptEngine"
        ? currentUser?.prompt_engine_limits_remaining
        : feature === "Conversation"
        ? currentUser?.conversation_limits_remaining
        : feature === "BrandAudit"
        ? currentUser?.brand_audit_limits_remaining
        : feature === "CategoryAnalysis"
        ? currentUser?.categoryanalysis_limits_remaining
        : feature === "BaselineCategoryAnalysis"
        ? currentUser?.baseline_categoryanalysis_limits_remaining
        : currentUser?.content_mapping_limits_remaining;

    apiCalls =
      feature === "Discovery"
        ? currentUser?.discovery_limits
        : feature === "BaselineDiscovery"
        ? currentUser?.baseline_discovery_limits
        : feature === "PromptEngine"
        ? currentUser?.prompt_engine_limits
        : feature === "Conversation"
        ? currentUser?.conversation_limits
        : feature === "BrandAudit"
        ? currentUser?.brand_audit_limits
        : feature === "CategoryAnalysis"
        ? currentUser?.categoryanalysis_limits
        : feature === "BaselineCategoryAnalysis"
        ? currentUser?.baseline_categoryanalysis_limits
        : currentUser?.content_mapping_limits;
  }

  const limitReached = remainingCalls === 0;

  const freq = currentUser?.limit_frequency;
  const freqLabel = freq === "non_reset" ? "" : freq;

  let launchText = `${remainingCalls} launch${
    remainingCalls !== 1 ? "es" : ""
  } remaining out of the ${apiCalls} launches${freqLabel ? ` ${freqLabel}` : ""} limit.`;
  let hoverText = null;

  if (isCustomerPool) {
    launchText =
      remainingCalls !== null
        ? `${remainingCalls} of ${apiCalls} launches remaining for your organization.`
        : "";
    hoverText =
      poolStatus?.user_used != null
        ? `You have personally used ${poolStatus.user_used} launch${
            poolStatus.user_used !== 1 ? "es" : ""
          }.`
        : null;
    if (limitReached) {
      launchText =
        freq === "non_reset"
          ? "Your organization has reached its limit."
          : `Your organization has reached its ${freq ?? "monthly"} limit.`;
    }
  } else {
    if (limitReached && !currentUser?.is_special) {
      launchText =
        freq === "non_reset"
          ? "You have reached your limit."
          : `You have reached your ${freq} limit. You may try again next ${
              freq === "daily" ? "day" : "month"
            }.`;
    }
  }

  if (loading || currentUser?.is_special) launchText = "";

  return {
    launchesRemaining: remainingCalls,
    launchText,
    hoverText,
    updateLaunches: updateUser,
    isSpecial: currentUser?.is_special || false,
    frequency: currentUser?.limit_frequency ?? "monthly",
    updatePersonasOnly,
    personas: currentUser?.personas ?? { "General Profile": "General Profile" },
    isCustomerPool,
    isBaselineDiscoveryEnabled: currentUser?.is_baseline_discovery_enabled || false,
    isBaselineCategoryAnalysisEnabled: currentUser?.is_baseline_categoryanalysis_enabled || false,
  };
}
